/**
 * LibraryGridView — responsive recipe card grid for the library landing (Wave 2).
 *
 * Replaces the old FlatRecipeList + Collection-navigation landing on the
 * library landing per design.md §6.3 Amendment 2026-05-10.
 *
 * Layout (R2 library-surface redesign — stat cards, comp Option 2):
 *   - 1 column below 768px
 *   - 2 columns at 768px+ (never 3-up — the taller stat cards top out at 2)
 *
 * Filter (library-surface redesign §C1):
 *   - A single typographic collections index (LibraryCollectionsIndex) replaces
 *     the old filled-accent pill row. Multi-select collections, OR-within.
 *   - The TAGS row is retired (R1 verdict): no tag line renders, with or without
 *     tag data. The faceting helper is unchanged; its tags axis is always empty.
 *   - Filtering is client-side only (library data already in memory).
 *
 * §2a discipline:
 *   - LibraryCollectionsIndex ALWAYS renders (even with zero collections) so the
 *     `Everything · + New` affordance stays discoverable (design.md §6.0a).
 *   - RecipeCard handles its own §2a internally.
 *
 * Design refs:
 *   design.md §6.3 — library grid, card layout.
 *   design.md §2 — accent reserved for signal elements; no shadows.
 *   design.md §4 — tokens.
 *   branding.md §5.2.1 — no banned words.
 *
 * CLAUDE.md Rule 7 — mobile + tablet + desktop from first commit.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Collection, Recipe } from "~/features/explorer/types";
import { usePipMoments } from "~/components/pip";
import { LibraryCollectionsIndex } from "./LibraryCollectionsIndex";
import { RecipeCard } from "./RecipeCard";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Count recipes per collection UUID, for the collection-index count. */
function countByCollection(recipes: Recipe[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const recipe of recipes) {
    for (const uuid of recipe.collectionUuids) {
      counts[uuid] = (counts[uuid] ?? 0) + 1;
    }
  }
  return counts;
}

/**
 * Q2 FACETING — AND across the two filter groups, OR within each group.
 *
 * A recipe passes when BOTH hold:
 *   - collections axis: no collection selected, OR the recipe belongs to at
 *     least one selected collection (OR-within / union).
 *   - tags axis: no tag selected, OR the recipe carries at least one selected
 *     tag (OR-within / union).
 *
 * Selecting a collection AND a tag intersects the two match sets (AND-across).
 *
 * NOTE: the tag axis is OR-within (`.some`) per collections-revival Q2 — this
 * changed from the previous AND-within (`.every`) behaviour so both axes are
 * consistent union-within / intersection-across.
 */
function filterByFacets(
  recipes: Recipe[],
  activeCollectionUuids: string[],
  activeTags: string[],
): Recipe[] {
  if (activeCollectionUuids.length === 0 && activeTags.length === 0) return recipes;
  return recipes.filter((recipe) => {
    const collectionMatch =
      activeCollectionUuids.length === 0 ||
      recipe.collectionUuids.some((c) => activeCollectionUuids.includes(c));
    const recipeTags = recipe.tags ?? [];
    const tagMatch = activeTags.length === 0 || recipeTags.some((t) => activeTags.includes(t));
    return collectionMatch && tagMatch;
  });
}

// ─── CSS injection ────────────────────────────────────────────────────────────

// R2 library-surface redesign (comp Option 2): stat cards are taller, so the
// grid tops out at 2-up — 1 column below 768px, 2 columns at ≥768px. The old
// 3-column ≥1024px block is deleted (never 3-up; that was the cramming ceiling
// the redesign removes). Mirrors `.cards` in `.plan/comps/library-comps.html`.
const GRID_CSS = `
.proof-library-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 12px 16px 24px;
}

@media (min-width: 768px) {
  .proof-library-grid {
    grid-template-columns: 1fr 1fr;
    padding: 16px 20px 32px;
  }
}
`;

if (typeof document !== "undefined") {
  const id = "proof-library-grid-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = GRID_CSS;
    document.head.appendChild(el);
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

interface LibraryGridViewProps {
  recipes: Recipe[];
  activeRecipeUuid: string | null;
  onActivateRecipe: (uuid: string) => void;
  /**
   * The user's collections — drives the COLLECTIONS filter row above the tags
   * row. Defaults to [] (the row still renders with All / + New collection).
   * Collections-revival Wave 2.
   */
  collections?: Collection[];
  /**
   * Called with the new collection name when the user creates one via the
   * "+ New collection" chip. Resolves on success / rejects on error (the modal
   * surfaces the inline error). When absent the "+ New collection" chip is not
   * rendered. Collections-revival Wave 2.
   */
  onCreateCollection?: (name: string) => Promise<void>;
  /**
   * Called when the user renames the single-selected collection from the index's
   * inline "Rename" affordance. Forwarded to LibraryCollectionsIndex →
   * CollectionRenameModal. Resolves on success / rejects on error (the modal
   * surfaces the inline error). When absent the "Rename" affordance is not
   * rendered.
   */
  onRenameCollection?: (uuid: string, newName: string) => Promise<void>;
  /**
   * Called when the user deletes the single-selected collection from the index's
   * inline "Delete" affordance. Forwarded to LibraryCollectionsIndex →
   * CollectionDeleteModal. Resolves on success / rejects on error (the modal
   * surfaces the inline error). When absent the "Delete" affordance is not
   * rendered.
   */
  onDeleteCollection?: (uuid: string) => Promise<void>;
  /**
   * PERF Wave 3a — F4: optional data-prefetch callback forwarded to RecipeCard.
   *
   * Called (after ~120 ms debounce in RecipeCard) on recipe-row hover so the
   * SWR cache is warm before the user clicks. Optional: card behaviour is
   * unchanged when absent.
   */
  onPrefetchRecipe?: (uuid: string) => void;
}

export function LibraryGridView({
  recipes,
  activeRecipeUuid,
  onActivateRecipe,
  collections = [],
  onCreateCollection,
  onRenameCollection,
  onDeleteCollection,
  onPrefetchRecipe,
}: LibraryGridViewProps) {
  // Collection filter axis (multi-select, OR-within / union). The TAGS axis is
  // no longer surfaced (R1 verdict — TagFilterChips stays hidden; the
  // typographic index renders no TAGS line), so the tags argument to the
  // unchanged faceting function is always empty. Collection faceting is intact.
  const [activeCollectionUuids, setActiveCollectionUuids] = useState<string[]>([]);

  // PERF (recipe-switch wave): memoise the two per-render derivations so an
  // unrelated re-render (a parent state change, a Pip moment) does not re-walk
  // every recipe twice. countByCollection re-runs only when `recipes` changes;
  // filterByFacets re-runs only when `recipes` or the active collection set
  // changes. The tags axis is always empty (R1 verdict), so the `[]` literal
  // lives inside the memo and is not a dependency.
  const collectionCounts = useMemo(() => countByCollection(recipes), [recipes]);
  const visibleRecipes = useMemo(
    () => filterByFacets(recipes, activeCollectionUuids, []),
    [recipes, activeCollectionUuids],
  );

  // PERF (recipe-switch wave): stabilise the two callbacks handed to each
  // RecipeCard so React.memo(RecipeCard) is not defeated by a fresh function
  // identity every render. The parent (routes/app.tsx) supplies `onActivateRecipe`
  // as an inline arrow — a new reference on each app.tsx render — which would
  // otherwise re-render every card. A ref mirror holds the latest callback while
  // the wrapper identity stays constant across renders (empty dep array). No
  // stale-closure risk: the wrapper reads `.current` at call time.
  const onActivateRecipeRef = useRef(onActivateRecipe);
  onActivateRecipeRef.current = onActivateRecipe;
  const stableActivate = useCallback((uuid: string) => onActivateRecipeRef.current(uuid), []);

  const onPrefetchRecipeRef = useRef(onPrefetchRecipe);
  onPrefetchRecipeRef.current = onPrefetchRecipe;
  const stablePrefetch = useCallback((uuid: string) => onPrefetchRecipeRef.current?.(uuid), []);

  // ── P2 — list-end greet (plan §4 P2 / 2026-06-10-pip-mascot-placement.md) ──
  // When the user scrolls to the end of the recipe grid, fire the `list-end`
  // moment through the governor, which plays `greet` once per session (the slot
  // is owned by PipMomentsProvider, mounted in routes/app.tsx — we never render
  // an animating <PipMascot> here). An IntersectionObserver on a zero-height
  // sentinel after the grid is the trigger; the governor's once-per-session
  // policy makes a second scroll-to-end a no-op (no replay).
  //
  // The greet's job is "you reached the END of your list" — it must follow a
  // real traversal, not fire unprompted on first paint. On a library that fits
  // WITHOUT scrolling, the sentinel is already intersecting at mount, so firing
  // on the first intersection would greet on load (off-brand). We therefore only
  // fire when the sentinel enters the viewport AFTER having been out of view:
  // `hasLeft` flips true on a non-intersecting entry, and the greet only fires on
  // an intersecting entry once that flag is set. A non-scrolling library
  // (sentinel visible from mount, never left) thus never greets; a real
  // scroll-to-end (sentinel was off-screen, scrolls in) does. The governor still
  // gates once-per-session on top.
  //
  // `hasLeft` lives INSIDE the effect (per-observer-instance closure), NOT in a
  // ref that outlives observer re-creations. The effect re-runs whenever the
  // visible count changes (a filter tap / delete / background revalidation
  // shortening the list re-creates the observer), and a fresh observer's initial
  // callback fires synchronously with the sentinel already intersecting. A
  // write-once ref that persisted `hasLeft=true` across that re-creation made the
  // new observer greet on its very first (no-scroll) intersection — the spurious
  // list-end greet. Resetting the flag per observer means the greet only fires on
  // a genuine false→true transition observed by the SAME observer instance.
  //
  // `fire` is referentially stable (memoised in the provider), so the observer
  // effect re-runs only when the visible count changes.
  const { fire } = usePipMoments();
  const listEndSentinelRef = useRef<HTMLDivElement | null>(null);
  const visibleCount = visibleRecipes.length;
  useEffect(() => {
    // No grid → nothing to greet at the end of. (Empty filter result, or a
    // single-row grid where the end is the top — still observed; the governor
    // gates frequency, not the sentinel.)
    if (visibleCount === 0) return;
    const sentinel = listEndSentinelRef.current;
    if (sentinel === null) return;
    if (typeof IntersectionObserver === "undefined") return;

    // Per-observer-instance latch. Fresh on every effect run so a re-created
    // observer whose sentinel is already intersecting cannot greet without a
    // genuine leave-then-enter traversal it observed itself.
    let hasLeft = false;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            // The sentinel left the viewport — a subsequent re-entry is now a
            // genuine scroll-to-end traversal, eligible to greet.
            hasLeft = true;
          } else if (hasLeft) {
            // Intersecting AND it had previously left → a real scroll-to-end.
            // Governor owns frequency (once-per-session) + the collision rule.
            fire("list-end");
          }
          // Intersecting but the flag is still false (sentinel visible from
          // mount, never scrolled away) → a non-scrolling library; do NOT greet.
        }
      },
      // rootMargin 0: fire when the sentinel actually enters the viewport.
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fire, visibleCount]);

  function handleToggleCollection(uuid: string) {
    if (uuid === "") {
      // Empty string signals "clear all" from the "All" chip.
      setActiveCollectionUuids([]);
      return;
    }
    setActiveCollectionUuids((prev) =>
      prev.includes(uuid)
        ? // De-select: remove from active set.
          prev.filter((u) => u !== uuid)
        : // Select: add to active set (OR-within / union).
          [...prev, uuid],
    );
  }

  return (
    <div>
      {/*
       * Typographic collections index (redesign §C1) — replaces the filled-accent
       * pill row. Always rendered (even with zero collections) so the
       * `Everything · + New` affordance stays discoverable (design.md §6.0a). The
       * TAGS line is intentionally absent (R1 verdict): the index renders no tag
       * row with or without tag data.
       */}
      <LibraryCollectionsIndex
        collections={collections}
        activeCollectionUuids={activeCollectionUuids}
        onToggleCollection={handleToggleCollection}
        counts={collectionCounts}
        {...(onCreateCollection !== undefined ? { onCreateCollection } : {})}
        {...(onRenameCollection !== undefined ? { onRenameCollection } : {})}
        {...(onDeleteCollection !== undefined ? { onDeleteCollection } : {})}
      />

      <div className="proof-library-grid" data-testid="library-grid">
        {visibleRecipes.map((recipe) => (
          <RecipeCard
            key={recipe.uuid}
            recipe={recipe}
            isActive={recipe.uuid === activeRecipeUuid}
            onClick={stableActivate}
            {...(onPrefetchRecipe !== undefined ? { onPrefetchRecipe: stablePrefetch } : {})}
          />
        ))}
      </div>

      {/*
       * P2 list-end sentinel — a zero-height, non-interactive marker at the end
       * of the recipe grid. When it scrolls into view the IntersectionObserver
       * above fires `list-end` and the governor plays `greet` once per session.
       * It carries NO visible chrome (no accent, no border) and reserves no
       * layout — Pip renders in the provider's top-centre slot, not here. Only
       * rendered when there is a grid to reach the end of (visibleCount > 0).
       */}
      {visibleCount > 0 && (
        <div
          ref={listEndSentinelRef}
          data-testid="library-list-end-sentinel"
          aria-hidden="true"
          style={{ height: 1, width: "100%" }}
        />
      )}
    </div>
  );
}
